/**
 * Monitoramento de Dispositivos — página principal.
 *
 * Navegação:
 *   Dashboard | Dispositivos | Vínculos pendentes | Importar XLSX | Cronograma |
 *   Monitoramentos | Histórico | Análises
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Cpu,
  Upload,
  CalendarDays,
  ClipboardCheck,
  History,
  BarChart3,
  Bell,
  Link2,
} from "lucide-react";

import DeviceMonitoringDashboard from "./Dashboard";
import DeviceList from "./DeviceList";
import ImportXLSX from "./ImportXLSX";
import Schedule from "./Schedule";
import Monitorings from "./Monitorings";
import HistoryPage from "./History";
import Analysis from "./Analysis";
import Alerts from "./Alerts";
import PendingLinks from "./PendingLinks";
import { useContextClientId } from "./hierarchy";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "devices", label: "Dispositivos", icon: Cpu },
  { key: "links", label: "Vínculos pendentes", icon: Link2 },
  { key: "import", label: "Importar XLSX", icon: Upload },
  { key: "schedule", label: "Cronograma", icon: CalendarDays },
  { key: "monitorings", label: "Monitoramentos", icon: ClipboardCheck },
  { key: "history", label: "Histórico", icon: History },
  { key: "alerts", label: "Alertas", icon: Bell },
  { key: "analysis", label: "Análises", icon: BarChart3 },
];

export default function DeviceMonitoring() {
  const [tab, setTab] = useState("dashboard");
  const contextClientId = useContextClientId();
  // Filtros globais compartilhados por Dashboard e Análises.
  const [filters, setFilters] = useState({ client_id: contextClientId, period: "30d" });
  useEffect(() => {
    setFilters((f) => ({ ...f, client_id: contextClientId, unit_id: "", area_id: "" }));
  }, [contextClientId]);
  const { user } = useAuth() || {};
  const [pendingLinks, setPendingLinks] = useState(0);

  // Alerta administrativo: órfãos não entram nos indicadores oficiais,
  // mas nunca ficam escondidos.
  useEffect(() => {
    if (!user || user.role === "cliente" || user.role === "tecnico") return;
    api
      .get("/device-monitoring/integrity")
      .then(({ data }) => setPendingLinks(data.pending_link || 0))
      .catch(() => setPendingLinks(0)); // aviso opcional: a aba exibe o erro real
  }, [user, tab]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Monitoramento de Dispositivos
        </h1>
        <p className="text-sm text-muted-foreground">
          Cadastro, planejamento, execução e análise de monitoramentos de
          dispositivos de controle de pragas.
        </p>
      </div>

      {pendingLinks > 0 && !["links", "devices"].includes(tab) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4" />
          {pendingLinks} dispositivo(s) sem vínculo válido de cliente/unidade
          não entram em cronogramas nem nos indicadores oficiais.
          <button onClick={() => setTab("links")} className="ml-auto underline font-medium">
            Ver pendências
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-border pb-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div>
        {tab === "dashboard" && (
          <DeviceMonitoringDashboard filters={filters} setFilters={setFilters} />
        )}
        {tab === "devices" && (
          <DeviceList onOpenPending={() => setTab("links")} />
        )}
        {tab === "links" && <PendingLinks />}
        {tab === "import" && <ImportXLSX />}
        {tab === "schedule" && <Schedule />}
        {tab === "monitorings" && <Monitorings />}
        {tab === "history" && <HistoryPage />}
        {tab === "alerts" && <Alerts />}
        {tab === "analysis" && <Analysis filters={filters} setFilters={setFilters} />}
      </div>
    </div>
  );
}
